import { useState, useEffect, useRef, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import type { Entitlements } from '@snap-platform/shared';
import { useAuth } from '../context/AuthContext.js';
import { useSnap } from '../hooks/useSnap.js';
import { SNAP_VERSION } from '../lib/snapConfig.js';
import { LATEST_SNAP_VERSION } from '../config/snapVersion.js';
import {
  getEntitlements,
  getSubscriptionStatus,
  getHealth,
} from '../services/api.js';
import { ManageBillingButton } from '../components/ManageBillingButton.js';
import { InstallSnapButton } from '../components/InstallSnapButton.js';
import {
  SearchIcon,
  MessageCircleIcon,
  ShieldCheckIcon,
  CreditCardIcon,
  PlugIcon,
  LifeBuoyIcon,
  ToolIcon,
  BookOpenIcon,
  ActivityIcon,
  LockIcon,
  WalletIcon,
} from '../components/Icons.js';

// ---------------------------------------------------------------------------
// Help Articles Knowledge Base
// ---------------------------------------------------------------------------
interface HelpArticle {
  id: string;
  category: string;
  title: string;
  content: string;
  keywords: string[];
  actionLink?: string;
  actionLabel?: string;
}

const HELP_ARTICLES: HelpArticle[] = [
  {
    id: 'billing-1',
    category: 'Billing',
    title: 'How do I subscribe to Pro?',
    content: 'Pro subscriptions are available on our pricing page. Upgrade to unlock 75 scans/month, risk-based warnings, and full contract analysis.',
    keywords: ['subscribe', 'pro', 'upgrade', 'payment', 'stripe', 'pay', 'price', 'cost'],
    actionLink: '/pricing',
    actionLabel: 'View Pricing',
  },
  {
    id: 'billing-2',
    category: 'Billing',
    title: 'How do I cancel my subscription?',
    content: 'You can manage your subscription through the Stripe customer portal. Your access continues until the end of your current billing period. We offer a 7-day money-back guarantee on your first payment.',
    keywords: ['cancel', 'subscription', 'unsubscribe', 'stop', 'end', 'refund'],
  },
  {
    id: 'billing-3',
    category: 'Billing',
    title: 'What payment methods are accepted?',
    content: 'We accept all major credit and debit cards through Stripe, including Visa, Mastercard, and American Express. All payments are processed securely via Stripe.',
    keywords: ['payment', 'credit card', 'debit', 'visa', 'mastercard', 'method'],
  },
  {
    id: 'billing-4',
    category: 'Billing',
    title: 'How do I get a refund?',
    content: 'We offer a 7-day money-back guarantee on Pro subscriptions. Visit our Refund Policy page or contact support within 7 days of your first payment. Refunds are processed back to your original payment method.',
    keywords: ['refund', 'money back', 'guarantee', 'return'],
    actionLink: '/refund-policy',
    actionLabel: 'View Refund Policy',
  },
  {
    id: 'snap-1',
    category: 'Snap',
    title: 'How do I install the CryptoGuardians Snap?',
    content: 'Connect your wallet on the Dashboard, then click "Install Snap". MetaMask will prompt you to approve the installation. The Snap runs inside MetaMask and scans transactions before you sign.',
    keywords: ['install', 'snap', 'metamask', 'setup', 'add'],
    actionLink: '/wallet',
    actionLabel: 'Go to Dashboard',
  },
  {
    id: 'snap-2',
    category: 'Snap',
    title: 'Why can\'t I install the Snap?',
    content: 'Make sure you have MetaMask Flask or MetaMask version 11.0+ installed. The Snap requires MetaMask\'s Snaps feature to be enabled. Try updating MetaMask to the latest version.',
    keywords: ['install', 'error', 'fail', 'metamask', 'flask', 'version', 'cannot', 'unable'],
  },
  {
    id: 'snap-3',
    category: 'Snap',
    title: 'What permissions does the Snap need?',
    content: 'The CryptoGuardians Snap requires permission to view transaction details before you sign. It is non-custodial and never has access to your private keys or funds.',
    keywords: ['permission', 'access', 'security', 'safe', 'trust', 'custodial'],
  },
  {
    id: 'recovery-1',
    category: 'Recovery',
    title: 'I lost access to my wallet. How do I recover my subscription?',
    content: 'Use the Recovery page to restore your subscription to a new wallet. You\'ll need access to the email address used for your Stripe billing. A verification code will be sent to confirm ownership.',
    keywords: ['lost', 'wallet', 'recover', 'restore', 'new wallet', 'transfer', 'access'],
    actionLink: '/recover',
    actionLabel: 'Restore Subscription',
  },
  {
    id: 'recovery-2',
    category: 'Recovery',
    title: 'How do I transfer my subscription to another wallet?',
    content: 'Visit the Transfer page to move your subscription. You\'ll need to verify your Stripe billing email to transfer the subscription to your new wallet address.',
    keywords: ['transfer', 'move', 'another wallet', 'different wallet', 'change wallet'],
    actionLink: '/transfer',
    actionLabel: 'Transfer Subscription',
  },
  {
    id: 'recovery-3',
    category: 'Recovery',
    title: 'My subscription shows as inactive but I paid.',
    content: 'Try running Diagnostics below to check your account status. If your subscription is active in Stripe but not linked, use the Restore flow to re-link it to your wallet.',
    keywords: ['inactive', 'paid', 'not working', 'missing', 'lost', 'gone', 'subscription'],
    actionLink: '/recover',
    actionLabel: 'Restore Subscription',
  },
  {
    id: 'troubleshooting-1',
    category: 'Troubleshooting',
    title: 'My scan count seems wrong.',
    content: 'Scan counts reset at the beginning of each billing period. Check the Dashboard for your exact usage and reset date. Free tier users get 5 scans/month, Pro users get 75.',
    keywords: ['scan', 'count', 'wrong', 'limit', 'quota', 'reset', 'usage'],
    actionLink: '/wallet',
    actionLabel: 'Check Dashboard',
  },
  {
    id: 'troubleshooting-2',
    category: 'Troubleshooting',
    title: 'The Snap isn\'t detecting threats.',
    content: 'Ensure the Snap is installed and your subscription is active. The Snap only analyzes transactions when you initiate them through MetaMask. Check your Dashboard to verify your protection status.',
    keywords: ['not working', 'detect', 'threat', 'scan', 'protection', 'inactive'],
    actionLink: '/wallet',
    actionLabel: 'Check Dashboard',
  },
  {
    id: 'troubleshooting-3',
    category: 'Troubleshooting',
    title: 'I\'m getting a "wallet not found" error.',
    content: 'This means your wallet hasn\'t been registered yet. Connect your wallet on the Connect page first, then try again. Make sure you\'re using the same wallet address.',
    keywords: ['wallet not found', 'error', 'connect', 'register', 'not found'],
  },
];

const CATEGORIES = ['Billing', 'Snap', 'Recovery', 'Troubleshooting'] as const;

const CATEGORY_ICONS: Record<string, typeof CreditCardIcon> = {
  'Billing': CreditCardIcon,
  'Snap': PlugIcon,
  'Recovery': LifeBuoyIcon,
  'Troubleshooting': ToolIcon,
};

// ---------------------------------------------------------------------------
// Chat Assistant
// ---------------------------------------------------------------------------
interface ChatMessage {
  role: 'user' | 'assistant';
  text: string;
  articles?: HelpArticle[];
}

function findArticles(query: string): HelpArticle[] {
  const q = query.toLowerCase();
  const words = q.split(/\s+/).filter((w) => w.length > 2);

  const scored = HELP_ARTICLES.map((article) => {
    let score = 0;
    const titleLower = article.title.toLowerCase();
    const contentLower = article.content.toLowerCase();

    for (const word of words) {
      if (titleLower.includes(word)) score += 3;
      if (contentLower.includes(word)) score += 1;
      if (article.keywords.some((k) => k.includes(word))) score += 2;
    }

    if (titleLower.includes(q)) score += 5;

    return { article, score };
  });

  return scored
    .filter((s) => s.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, 3)
    .map((s) => s.article);
}

// ---------------------------------------------------------------------------
// Status dot helper
// ---------------------------------------------------------------------------
function StatusDot({ status }: { status: 'green' | 'yellow' | 'red' }) {
  return <span className={`sp-status-dot sp-status-dot-${status}`} aria-hidden="true" />;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export function Support() {
  const { isAuthenticated, token, connectWallet, isConnecting } = useAuth();
  const snap = useSnap(token);

  const [entitlements, setEntitlements] = useState<Entitlements | null>(null);
  const [subStatus, setSubStatus] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;
    getEntitlements(token).then(setEntitlements).catch(() => {});
    getSubscriptionStatus(token).then((s) => setSubStatus(s.status)).catch(() => {});
  }, [token]);

  // Diagnostics
  const diagRef = useRef<HTMLDivElement>(null);
  const [diagLoading, setDiagLoading] = useState(false);
  const [diagResult, setDiagResult] = useState<{
    entitlements: Entitlements | null;
    subStatus: string;
    apiHealth: boolean;
    snapInstalled: boolean;
    snapVersion: string | null;
    walletConnected: boolean;
  } | null>(null);
  const [copied, setCopied] = useState(false);

  // Search
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<HelpArticle[]>([]);
  const [expandedArticle, setExpandedArticle] = useState<string | null>(null);
  const [activeCategory, setActiveCategory] = useState<string | null>(null);

  // Chat
  const [chatOpen, setChatOpen] = useState(false);
  const [chatInput, setChatInput] = useState('');
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([
    {
      role: 'assistant',
      text: 'Tell me what you\'re trying to do: install snap, restore pro, billing, scans, transfer\u2026',
    },
  ]);
  const chatEndRef = useRef<HTMLDivElement>(null);

  // System status
  const [systemStatus, setSystemStatus] = useState<'checking' | 'operational' | 'degraded'>('checking');

  useEffect(() => {
    getHealth()
      .then(() => setSystemStatus('operational'))
      .catch(() => setSystemStatus('degraded'));
  }, []);

  // Derived state
  const isPro = entitlements?.tier === 'pro';
  const isSubActive = subStatus === 'active';
  const snapOk = snap.isInstalled;
  const fullyProtected = isPro && isSubActive && snapOk;
  const partiallyProtected = isPro && !snapOk;

  const heroStatus: 'green' | 'amber' | 'red' = !isAuthenticated
    ? 'red'
    : fullyProtected
      ? 'green'
      : 'amber';

  const scansRemaining = entitlements?.scansRemaining ?? null;
  const scanLimit = entitlements?.monthlyLimit ?? (isPro ? 75 : 5);

  // Search handler
  function handleSearch(query: string) {
    setSearchQuery(query);
    setActiveCategory(null);
    if (query.trim().length < 2) {
      setSearchResults([]);
      return;
    }
    setSearchResults(findArticles(query));
  }

  // Category filter
  function handleCategoryClick(cat: string) {
    if (activeCategory === cat) {
      setActiveCategory(null);
      setSearchResults([]);
      setSearchQuery('');
    } else {
      setActiveCategory(cat);
      setSearchResults(HELP_ARTICLES.filter((a) => a.category === cat));
      setSearchQuery('');
    }
  }

  // Chat handler
  function handleChatSend(e: FormEvent) {
    e.preventDefault();
    const q = chatInput.trim();
    if (!q) return;

    const userMsg: ChatMessage = { role: 'user', text: q };
    const qLower = q.toLowerCase();
    const articles = findArticles(q);

    let responseText: string;
    let responseArticles: HelpArticle[] | undefined;

    // Build user context string
    const userContext = isAuthenticated
      ? `\n\nYour status: ${isPro ? 'Pro' : 'Free'} plan, ${snapOk ? 'Snap installed' : 'Snap not installed'}, ${scansRemaining ?? '\u2014'} scans remaining.`
      : '';

    if (/\b(pro|upgrade|plan|tier)\b/.test(qLower)) {
      if (!isAuthenticated) {
        responseText = 'Connect your wallet first so I can check your account status.';
      } else if (isPro && isSubActive) {
        responseText = `You're on the Pro plan with an active subscription. ${scansRemaining ?? '\u2014'} scans remaining this period. Everything looks good!`;
      } else if (isPro) {
        responseText = 'Your account shows Pro tier but your subscription may need attention. Try restoring your subscription.';
      } else {
        responseText = 'You\'re currently on the Free plan (5 scans/month). Upgrade to Pro for 75 scans/month, risk-based warnings, and full contract analysis.' + userContext;
      }
      responseArticles = articles.length > 0 ? articles : undefined;
    } else if (/\b(snap|install|metamask)\b/.test(qLower)) {
      if (snapOk) {
        responseText = `Your CryptoGuardians Snap is installed${SNAP_VERSION ? ` (v${SNAP_VERSION})` : ''}. It's actively protecting your transactions.` + userContext;
      } else if (snap.isMetaMask) {
        responseText = 'Your Snap isn\'t installed yet. Click "Install Snap" in the Instant Fix grid above to add transaction protection.' + userContext;
      } else {
        responseText = 'You\'ll need MetaMask to install the CryptoGuardians Snap. Download MetaMask first, then come back to install.';
      }
      responseArticles = articles.length > 0 ? articles : undefined;
    } else if (/\b(billing|charge|payment|refund|cancel)\b/.test(qLower)) {
      if (isPro) {
        responseText = 'Your Pro subscription is managed through Stripe. You can view invoices and manage billing in the Stripe customer portal. We offer a 7-day money-back guarantee.' + userContext;
      } else {
        responseText = 'You\'re on the free plan so there are no active charges. If you previously had a Pro subscription, try restoring it.' + userContext;
      }
      responseArticles = articles.length > 0 ? articles : undefined;
    } else if (/\b(recover|restore|lost|transfer|wallet|move)\b/.test(qLower)) {
      responseText = 'Use Restore if you\'re reconnecting the same wallet or re-linking Pro on a fresh browser. Use Transfer if you\'re moving to a completely new wallet address. See the "Restore vs Transfer" section above for details.' + userContext;
      responseArticles = articles.length > 0 ? articles : undefined;
    } else if (/\b(scan|quota|limit|usage)\b/.test(qLower)) {
      if (entitlements) {
        responseText = `You've used ${entitlements.scansUsedThisMonth} of ${entitlements.monthlyLimit} scans this month. ${entitlements.scansRemaining} remaining. Quota resets on ${new Date(entitlements.quotaResetsAt).toLocaleDateString()}.`;
      } else if (!isAuthenticated) {
        responseText = 'Connect your wallet so I can check your scan usage.';
      } else {
        responseText = 'I couldn\'t load your scan data. Try running Diagnostics.';
      }
      responseArticles = articles.length > 0 ? articles : undefined;
    } else if (/\b(safe|security|trust|privacy|custodial|keys)\b/.test(qLower)) {
      responseText = 'CryptoGuardians is fully non-custodial. We never access your private keys, seed phrase, or funds. The Snap only reads transaction data to detect threats \u2014 it cannot sign or modify transactions.';
      responseArticles = articles.length > 0 ? articles : undefined;
    } else if (/\b(diagnos|status|check|health|report)\b/.test(qLower)) {
      responseText = 'Scroll up and click "Run Diagnostics" in the Instant Fix grid, or scroll down to the System Report section to see your full account report.' + userContext;
    } else if (/\b(help|hi|hello|hey|support)\b/.test(qLower)) {
      responseText = 'I\'m here to help! You can ask me about:\n\n' +
        '\u2022 Your subscription or billing\n' +
        '\u2022 Installing the Snap\n' +
        '\u2022 Scan quota and usage\n' +
        '\u2022 Account recovery\n' +
        '\u2022 Security and privacy\n\n' +
        'Or use the Instant Fix grid and search bar above.' + userContext;
    } else if (articles.length > 0) {
      responseText = `I found ${articles.length} article${articles.length > 1 ? 's' : ''} that might help:`;
      responseArticles = articles;
    } else {
      responseText = 'I couldn\'t find a specific answer for that. Try:\n\n' +
        '\u2022 Run Diagnostics to check your account\n' +
        '\u2022 Browse the Knowledge Base categories\n' +
        '\u2022 Visit /recover if you lost wallet access';
    }

    const assistantMsg: ChatMessage = {
      role: 'assistant',
      text: responseText,
      articles: responseArticles,
    };

    setChatMessages((prev) => [...prev, userMsg, assistantMsg]);
    setChatInput('');
  }

  // Quick reply from chat
  function handleQuickReply(text: string) {
    setChatInput(text);
    // Simulate form submit
    const fakeEvent = { preventDefault: () => {} } as FormEvent;
    setChatInput('');
    const articles = findArticles(text);
    const userMsg: ChatMessage = { role: 'user', text };
    // Reuse same routing logic — trigger via chatInput update
    const qLower = text.toLowerCase();
    let responseText = '';
    let responseArticles: HelpArticle[] | undefined;
    const userContext = isAuthenticated
      ? `\n\nYour status: ${isPro ? 'Pro' : 'Free'} plan, ${snapOk ? 'Snap installed' : 'Snap not installed'}, ${scansRemaining ?? '\u2014'} scans remaining.`
      : '';

    if (/snap|install/.test(qLower)) {
      responseText = snapOk
        ? `Snap is already installed${SNAP_VERSION ? ` (v${SNAP_VERSION})` : ''}. You're all set!` + userContext
        : 'Click "Install Snap" in the Instant Fix grid above to add transaction protection to MetaMask.' + userContext;
      responseArticles = articles.length > 0 ? articles : undefined;
    } else if (/upgrade|pro/.test(qLower)) {
      responseText = isPro
        ? 'You\'re already on Pro! Everything looks good.' + userContext
        : 'Upgrade to Pro for 75 scans/month and full protection.' + userContext;
      responseArticles = articles.length > 0 ? articles : undefined;
    } else if (/restore/.test(qLower)) {
      responseText = 'Use the Restore flow to re-link your Pro subscription. This works when you have the same wallet but switched browsers or reinstalled MetaMask.' + userContext;
      responseArticles = articles.length > 0 ? articles : undefined;
    } else if (/transfer/.test(qLower)) {
      responseText = 'Use Transfer when moving your subscription to a completely new wallet address.' + userContext;
      responseArticles = articles.length > 0 ? articles : undefined;
    } else if (/diagnos/.test(qLower)) {
      responseText = 'Scroll to the System Report section below or click "Run Diagnostics" in the Instant Fix grid.' + userContext;
    } else {
      responseText = articles.length > 0 ? `Here's what I found:` : 'Try browsing the Knowledge Base categories above.';
      responseArticles = articles.length > 0 ? articles : undefined;
    }

    setChatMessages((prev) => [...prev, userMsg, { role: 'assistant', text: responseText, articles: responseArticles }]);
    void fakeEvent;
  }

  // Scroll chat to bottom
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  // Diagnostics
  async function runDiagnostics() {
    if (!token) return;
    setDiagLoading(true);
    setDiagResult(null);

    let ent: Entitlements | null = null;
    let sub = 'unknown';
    let apiHealth = false;

    try {
      ent = await getEntitlements(token);
      setEntitlements(ent);
    } catch { /* no-op */ }

    try {
      const s = await getSubscriptionStatus(token);
      sub = s.status;
      setSubStatus(sub);
    } catch { /* no-op */ }

    try {
      await getHealth();
      apiHealth = true;
    } catch { /* no-op */ }

    setDiagResult({
      entitlements: ent,
      subStatus: sub,
      apiHealth,
      snapInstalled: snap.isInstalled,
      snapVersion: SNAP_VERSION ?? null,
      walletConnected: true,
    });
    setDiagLoading(false);

    // Scroll to diagnostics
    setTimeout(() => diagRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
  }

  // Copy diagnostics report
  function copyReport() {
    if (!diagResult) return;
    const lines = [
      'CryptoGuardians System Report',
      '---',
      `Wallet: Connected`,
      `Plan: ${diagResult.entitlements?.tier === 'pro' ? 'Pro' : 'Free'}`,
      `Subscription: ${diagResult.subStatus}`,
      `Snap: ${diagResult.snapInstalled ? 'Installed' : 'Not Installed'}`,
      diagResult.snapInstalled && diagResult.snapVersion ? `Snap Version: v${diagResult.snapVersion}` : null,
      diagResult.entitlements ? `Scans: ${diagResult.entitlements.scansUsedThisMonth} / ${diagResult.entitlements.monthlyLimit}` : null,
      diagResult.entitlements ? `Remaining: ${diagResult.entitlements.scansRemaining}` : null,
      diagResult.entitlements ? `Resets: ${new Date(diagResult.entitlements.quotaResetsAt).toLocaleDateString()}` : null,
      `API: ${diagResult.apiHealth ? 'Operational' : 'Unreachable'}`,
      '---',
      `Generated: ${new Date().toISOString()}`,
    ].filter(Boolean).join('\n');

    navigator.clipboard.writeText(lines).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  // FAQ ref for scroll
  const faqRef = useRef<HTMLDivElement>(null);

  return (
    <div className="support-page">
      <div className="container">

        {/* ============================================================= */}
        {/* PART 1 — HERO: GET HELP FAST + STATUS                        */}
        {/* ============================================================= */}
        <div className={`dash-card sp-hero sp-hero-${heroStatus}`}>
          <div className="sp-hero-content">
            <ShieldCheckIcon className="sp-hero-icon" aria-hidden="true" />
            <div>
              <h1 className="sp-hero-title">
                {!isAuthenticated
                  ? 'Connect Wallet to Get Started'
                  : fullyProtected
                    ? 'You Are Fully Protected'
                    : partiallyProtected
                      ? 'Complete Your Protection'
                      : 'Get Help Fast'}
              </h1>
              <p className="sp-hero-desc">
                {!isAuthenticated
                  ? 'Connect your wallet to run diagnostics and access self-service tools.'
                  : fullyProtected
                    ? 'CryptoGuardians is actively protecting your wallet.'
                    : partiallyProtected
                      ? 'Install the CryptoGuardians Snap to activate real-time protection.'
                      : 'Upgrade to Pro and install the Snap to activate full protection.'}
              </p>
            </div>
          </div>

          {isAuthenticated && (
            <div className="sp-hero-status-row">
              <span className="sp-hero-status-item">
                <StatusDot status={isPro ? 'green' : 'red'} />
                Plan: {isPro ? 'Pro' : 'Free'}
              </span>
              <span className="sp-hero-status-item">
                <StatusDot status={snapOk ? 'green' : 'yellow'} />
                Snap: {snapOk ? 'Installed' : 'Not Installed'}
              </span>
              {entitlements && (
                <span className="sp-hero-status-item">
                  <StatusDot status={(entitlements.scansRemaining ?? 0) > 0 ? 'green' : 'red'} />
                  Scans: {entitlements.scansRemaining} / {scanLimit}
                </span>
              )}
            </div>
          )}

          <div className="sp-hero-actions">
            {!isAuthenticated ? (
              <button className="btn btn-primary" onClick={connectWallet} disabled={isConnecting}>
                {isConnecting ? 'Connecting...' : 'Connect Wallet'}
              </button>
            ) : (
              <>
                {!isPro && (
                  <a className="btn btn-primary btn-sm" href="/pricing">View Pricing</a>
                )}
                {!snapOk && snap.isMetaMask && (
                  <button className="btn btn-secondary btn-sm" onClick={snap.installSnap} disabled={snap.isLoading}>
                    {snap.isLoading ? 'Installing...' : 'Install Snap'}
                  </button>
                )}
                {!isPro && (
                  <Link to="/recover" className="btn btn-secondary btn-sm">Restore Pro</Link>
                )}
                <button className="btn btn-secondary btn-sm" onClick={runDiagnostics} disabled={diagLoading}>
                  {diagLoading ? 'Running...' : 'Run Diagnostics'}
                </button>
                {isPro && <ManageBillingButton />}
              </>
            )}
          </div>
        </div>

        {/* ============================================================= */}
        {/* PART 2 — INSTANT FIX GRID                                     */}
        {/* ============================================================= */}
        <div className="dash-card">
          <div className="dash-card-header">
            <ToolIcon className="dash-section-icon" aria-hidden="true" />
            <h2>Instant Fix</h2>
          </div>
          <div className="sp-fix-grid">
            <div className="sp-fix-card sp-fix-card-snap">
              <PlugIcon className="sp-fix-icon" aria-hidden="true" />
              <h4>Install Snap</h4>
              <p>{snapOk ? 'Already installed' : 'Add to MetaMask'}</p>
              {!snapOk && <InstallSnapButton variant="compact" />}
            </div>
            <a href={isPro ? '#' : '/pricing'} className="sp-fix-card" style={{ textDecoration: 'none' }}>
              <CreditCardIcon className="sp-fix-icon" aria-hidden="true" />
              <h4>{isPro ? 'Pro Active' : 'Upgrade to Pro'}</h4>
              <p>{isPro ? 'Already on Pro' : 'Unlock full protection'}</p>
            </a>
            <Link to="/recover" className="sp-fix-card">
              <LifeBuoyIcon className="sp-fix-icon" aria-hidden="true" />
              <h4>Restore Subscription</h4>
              <p>Re-link via email</p>
            </Link>
            <Link to="/transfer" className="sp-fix-card">
              <WalletIcon className="sp-fix-icon" aria-hidden="true" />
              <h4>Transfer Subscription</h4>
              <p>Move to new wallet</p>
            </Link>
            <button
              className="sp-fix-card"
              onClick={runDiagnostics}
              disabled={diagLoading || !isAuthenticated}
            >
              <ToolIcon className="sp-fix-icon" aria-hidden="true" />
              <h4>Run Diagnostics</h4>
              <p>{diagLoading ? 'Running...' : 'Check account status'}</p>
            </button>
            <button
              className="sp-fix-card"
              onClick={() => faqRef.current?.scrollIntoView({ behavior: 'smooth' })}
            >
              <BookOpenIcon className="sp-fix-icon" aria-hidden="true" />
              <h4>Common Issues</h4>
              <p>Browse FAQ below</p>
            </button>
          </div>
          {snap.error && (
            <p className="error-message" style={{ marginTop: '0.75rem' }}>{snap.error}</p>
          )}
          {!isAuthenticated && (
            <p className="sp-fix-hint">
              <Link to="/connect">Connect your wallet</Link> to use diagnostic tools.
            </p>
          )}
        </div>

        {/* ============================================================= */}
        {/* PART 3 — DIAGNOSTICS / SYSTEM REPORT                          */}
        {/* ============================================================= */}
        <div className="dash-card" ref={diagRef}>
          <div className="dash-card-header">
            <ActivityIcon className="dash-section-icon" aria-hidden="true" />
            <h2>System Report</h2>
          </div>
          {!isAuthenticated ? (
            <div className="sp-report-disabled">
              <LockIcon className="sp-report-disabled-icon" aria-hidden="true" />
              <p>Connect your wallet to generate a full system report.</p>
            </div>
          ) : !diagResult ? (
            <div className="sp-report-empty">
              <p>Click "Run Diagnostics" to generate your system report.</p>
              <button className="btn btn-primary btn-sm" onClick={runDiagnostics} disabled={diagLoading}>
                {diagLoading ? 'Running...' : 'Run Diagnostics'}
              </button>
            </div>
          ) : (
            <>
              <div className="dash-kv-list">
                <div className="dash-kv-row">
                  <span className="dash-kv-label">Wallet</span>
                  <span className="dash-kv-value sp-status-inline">
                    <StatusDot status="green" />
                    Connected
                  </span>
                </div>
                <div className="dash-kv-row">
                  <span className="dash-kv-label">Plan</span>
                  <span className="dash-kv-value sp-status-inline">
                    <StatusDot status={diagResult.entitlements?.tier === 'pro' ? 'green' : 'yellow'} />
                    {diagResult.entitlements?.tier === 'pro' ? 'Pro' : 'Free'}
                  </span>
                </div>
                <div className="dash-kv-row">
                  <span className="dash-kv-label">Snap</span>
                  <span className="dash-kv-value sp-status-inline">
                    <StatusDot status={diagResult.snapInstalled ? 'green' : 'yellow'} />
                    {diagResult.snapInstalled ? 'Installed' : 'Not Installed'}
                  </span>
                </div>
                {diagResult.snapInstalled && diagResult.snapVersion && (
                  <div className="dash-kv-row">
                    <span className="dash-kv-label">Snap Version</span>
                    <span className="dash-kv-value">v{diagResult.snapVersion}</span>
                  </div>
                )}
                <div className="dash-kv-row">
                  <span className="dash-kv-label">Subscription</span>
                  <span className="dash-kv-value sp-status-inline">
                    <StatusDot status={diagResult.subStatus === 'active' ? 'green' : 'yellow'} />
                    {diagResult.subStatus === 'active' ? 'Active' : diagResult.subStatus}
                  </span>
                </div>
                {diagResult.entitlements && (
                  <>
                    <div className="dash-kv-row">
                      <span className="dash-kv-label">Scans Used</span>
                      <span className="dash-kv-value">
                        {diagResult.entitlements.scansUsedThisMonth} / {diagResult.entitlements.monthlyLimit}
                      </span>
                    </div>
                    <div className="dash-kv-row">
                      <span className="dash-kv-label">Scans Remaining</span>
                      <span className="dash-kv-value sp-status-inline">
                        <StatusDot status={diagResult.entitlements.scansRemaining > 0 ? 'green' : 'red'} />
                        {diagResult.entitlements.scansRemaining}
                      </span>
                    </div>
                    <div className="dash-kv-row">
                      <span className="dash-kv-label">Reset Date</span>
                      <span className="dash-kv-value">
                        {new Date(diagResult.entitlements.quotaResetsAt).toLocaleDateString()}
                      </span>
                    </div>
                  </>
                )}
                <div className="dash-kv-row">
                  <span className="dash-kv-label">API Health</span>
                  <span className="dash-kv-value sp-status-inline">
                    <StatusDot status={diagResult.apiHealth ? 'green' : 'red'} />
                    {diagResult.apiHealth ? 'Operational' : 'Unreachable'}
                  </span>
                </div>
              </div>
              <button className="btn btn-secondary btn-sm" onClick={copyReport} style={{ marginTop: '1rem' }}>
                {copied ? 'Copied!' : 'Copy Report'}
              </button>
            </>
          )}
        </div>

        {/* ============================================================= */}
        {/* SNAP UPGRADE CARD                                              */}
        {/* ============================================================= */}
        {snapOk && SNAP_VERSION && SNAP_VERSION < LATEST_SNAP_VERSION && (
          <div className="dash-card snap-upgrade-card">
            <div className="dash-card-header">
              <PlugIcon className="dash-section-icon" aria-hidden="true" />
              <h2>Snap Update Available</h2>
            </div>
            <div className="snap-upgrade-versions">
              <div className="snap-upgrade-row">
                <span className="dash-kv-label">Your Version</span>
                <span className="dash-kv-value snap-upgrade-old">v{SNAP_VERSION}</span>
              </div>
              <div className="snap-upgrade-row">
                <span className="dash-kv-label">Latest Version</span>
                <span className="dash-kv-value snap-upgrade-new">v{LATEST_SNAP_VERSION}</span>
              </div>
            </div>
            <button
              className="btn btn-primary btn-sm snap-upgrade-button"
              onClick={snap.installSnap}
              disabled={snap.isLoading}
            >
              {snap.isLoading ? 'Updating...' : 'Update Snap'}
            </button>
          </div>
        )}

        {/* ============================================================= */}
        {/* PART 4 — RESTORE VS TRANSFER EDUCATION                        */}
        {/* ============================================================= */}
        <div className="dash-card">
          <div className="dash-card-header">
            <LifeBuoyIcon className="dash-section-icon" aria-hidden="true" />
            <h2>Restore vs Transfer \u2014 Which Do I Use?</h2>
          </div>
          <div className="sp-edu-grid">
            <div className="sp-edu-card">
              <h3>Restore Subscription</h3>
              <p className="sp-edu-when">Use when:</p>
              <ul className="sp-edu-list">
                <li>Same wallet address</li>
                <li>New computer or browser</li>
                <li>Reinstalling MetaMask</li>
                <li>Need to re-link Pro status</li>
              </ul>
              <Link to="/recover" className="btn btn-primary btn-sm" style={{ marginTop: 'auto' }}>
                Restore Subscription
              </Link>
            </div>
            <div className="sp-edu-card">
              <h3>Transfer Subscription</h3>
              <p className="sp-edu-when">Use when:</p>
              <ul className="sp-edu-list">
                <li>Switching to a new wallet address</li>
                <li>Lost access to old wallet</li>
                <li>Migrating wallets</li>
              </ul>
              <Link to="/transfer" className="btn btn-primary btn-sm" style={{ marginTop: 'auto' }}>
                Transfer Subscription
              </Link>
            </div>
          </div>
        </div>

        {/* ============================================================= */}
        {/* PART 5 — KNOWLEDGE BASE                                       */}
        {/* ============================================================= */}
        <div className="dash-card" ref={faqRef}>
          <div className="dash-card-header">
            <BookOpenIcon className="dash-section-icon" aria-hidden="true" />
            <h2>Knowledge Base</h2>
          </div>

          {/* Search */}
          <div className="sp-search-wrap-inline">
            <SearchIcon className="sp-search-icon" aria-hidden="true" />
            <input
              type="text"
              className="sp-search-input"
              placeholder="Search help articles..."
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
            />
          </div>

          {/* Category pills */}
          <div className="sp-category-pills">
            {CATEGORIES.map((cat) => {
              const Icon = CATEGORY_ICONS[cat] ?? BookOpenIcon;
              return (
                <button
                  key={cat}
                  className={`sp-pill ${activeCategory === cat ? 'sp-pill-active' : ''}`}
                  onClick={() => handleCategoryClick(cat)}
                >
                  <Icon className="sp-pill-icon" aria-hidden="true" />
                  {cat}
                </button>
              );
            })}
          </div>

          {/* No results */}
          {searchQuery && searchResults.length === 0 && searchQuery.length >= 2 && (
            <p className="sp-no-results">No articles found. Try different keywords or use the assistant.</p>
          )}

          {/* Articles accordion */}
          {(searchResults.length > 0 ? searchResults : !searchQuery && !activeCategory ? HELP_ARTICLES : []).map((article) => (
            <div
              key={article.id}
              className={`sp-faq-item ${expandedArticle === article.id ? 'sp-faq-open' : ''}`}
            >
              <button
                className="sp-faq-trigger"
                onClick={() => setExpandedArticle(expandedArticle === article.id ? null : article.id)}
                aria-expanded={expandedArticle === article.id}
              >
                <span className="sp-faq-cat">{article.category}</span>
                <span className="sp-faq-q">{article.title}</span>
                <span className="sp-faq-chevron" aria-hidden="true">{expandedArticle === article.id ? '\u2212' : '+'}</span>
              </button>
              {expandedArticle === article.id && (
                <div className="sp-faq-answer">
                  <p>{article.content}</p>
                  <div className="sp-faq-actions">
                    {article.actionLink && (
                      <Link to={article.actionLink} className="sp-faq-action-link">{article.actionLabel ?? 'Try this now'}</Link>
                    )}
                    {isAuthenticated && (
                      <button className="sp-faq-action-link" onClick={runDiagnostics}>Run Diagnostics</button>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* ============================================================= */}
        {/* PART 7 — TRUST STRIP                                          */}
        {/* ============================================================= */}
        <div className="sp-trust-strip">
          <div className="sp-trust-items">
            <span className="sp-trust-badge">
              <ShieldCheckIcon className="sp-trust-badge-icon" aria-hidden="true" />
              Non-custodial
            </span>
            <span className="sp-trust-badge">
              <LockIcon className="sp-trust-badge-icon" aria-hidden="true" />
              No fund access
            </span>
            <span className="sp-trust-badge">
              <PlugIcon className="sp-trust-badge-icon" aria-hidden="true" />
              Runs in MetaMask
            </span>
          </div>
          <p className="sp-trust-text">
            We can't access your wallet \u2014 we only help you evaluate risk before you sign.
          </p>
        </div>

        {/* System Status */}
        <div className="dash-card">
          <div className="dash-card-header">
            <ActivityIcon className="dash-section-icon" aria-hidden="true" />
            <h2>System Status</h2>
          </div>
          <div className="sp-status-grid">
            <div className="sp-status-item">
              <span className="dash-kv-label">API</span>
              {systemStatus === 'checking' ? (
                <span className="sp-status-neutral">Checking...</span>
              ) : systemStatus === 'operational' ? (
                <span className="sp-status-ok"><StatusDot status="green" /> Operational</span>
              ) : (
                <span className="sp-status-warn"><StatusDot status="red" /> Degraded</span>
              )}
            </div>
            <div className="sp-status-item">
              <span className="dash-kv-label">MetaMask Snap</span>
              {snapOk ? (
                <span className="sp-status-ok"><StatusDot status="green" /> Installed</span>
              ) : (
                <span className="sp-status-neutral"><StatusDot status="yellow" /> Not installed</span>
              )}
            </div>
            <div className="sp-status-item">
              <span className="dash-kv-label">Wallet</span>
              {isAuthenticated ? (
                <span className="sp-status-ok"><StatusDot status="green" /> Connected</span>
              ) : (
                <span className="sp-status-neutral"><StatusDot status="yellow" /> Not connected</span>
              )}
            </div>
          </div>
        </div>

      </div>

      {/* ============================================================= */}
      {/* PART 6 — CHAT ASSISTANT                                        */}
      {/* ============================================================= */}
      <div className={`sp-chat-widget ${chatOpen ? 'sp-chat-open' : ''}`}>
        {chatOpen && (
          <div className="sp-chat-window">
            <div className="sp-chat-header">
              <ShieldCheckIcon className="sp-chat-header-icon" aria-hidden="true" />
              <span>CryptoGuardians Assistant</span>
              <button className="sp-chat-close" onClick={() => setChatOpen(false)} aria-label="Close chat">
                &times;
              </button>
            </div>
            <div className="sp-chat-messages">
              {chatMessages.map((msg, i) => (
                <div key={i} className={`sp-chat-msg sp-chat-msg-${msg.role}`}>
                  <p>{msg.text}</p>
                  {msg.articles && msg.articles.length > 0 && (
                    <div className="sp-chat-articles">
                      {msg.articles.map((article) => (
                        <button
                          key={article.id}
                          className="sp-chat-article-link"
                          onClick={() => {
                            setExpandedArticle(article.id);
                            setChatOpen(false);
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                            handleSearch(article.title);
                          }}
                        >
                          {article.title}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              <div ref={chatEndRef} />
            </div>
            {/* Quick replies */}
            <div className="sp-chat-quick">
              {['Install Snap', 'Upgrade', 'Restore', 'Transfer', 'Diagnostics'].map((label) => (
                <button key={label} className="sp-chat-quick-btn" onClick={() => handleQuickReply(label)}>
                  {label}
                </button>
              ))}
            </div>
            <form className="sp-chat-input-wrap" onSubmit={handleChatSend}>
              <input
                type="text"
                className="sp-chat-input"
                placeholder="Type your question..."
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
              />
              <button type="submit" className="sp-chat-send" disabled={!chatInput.trim()}>
                Send
              </button>
            </form>
          </div>
        )}
        <button
          className="sp-chat-fab"
          onClick={() => setChatOpen(!chatOpen)}
          aria-label={chatOpen ? 'Close support chat' : 'Open support chat'}
        >
          <MessageCircleIcon className="sp-chat-fab-icon" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}
